import type { ReactNode } from "react";

import dayjs from "dayjs";

import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberWithPrefixFilter } from "app/filters/ngFilters";
import type { CompanyCard } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import { formatPercent } from "app/pages/sales-intelligence/utils/numbers";

export type FieldAnnotation = {
    iconName: IconName;
    content: string;
};

export type FieldAnnotationContext = {
    data: CompanyCard;
};

export type FieldsConfig = {
    key: keyof CompanyCard;
    render?: (value: unknown) => string;
    badge?: ReactNode;
    getAnnotation?: (ctx: FieldAnnotationContext) => FieldAnnotation | undefined;
};

const defineField = <K extends keyof CompanyCard>(config: {
    key: K;
    render?: (value: CompanyCard[K]) => string;
    badge?: ReactNode;
    getAnnotation?: (ctx: FieldAnnotationContext) => FieldAnnotation | undefined;
}): FieldsConfig => config as FieldsConfig;

const renderSiteTypes = (value: string[] | undefined): string =>
    value?.length ? value.map((type) => type.charAt(0).toUpperCase() + type.slice(1)).join(" & ") : "";

const renderEcommerceType = (value: string): string => value.replace("/", " & ") || "";

const renderFundingAmount = (value: number | undefined): string =>
    value != null ? String(abbrNumberWithPrefixFilter()(value, "$")) : "";

const renderFundingDate = (value: string | undefined): string => (value ? dayjs(value).format("MMM D, YYYY") : "");

const FORMAT_DECIMAL_PLACE = 1;
const renderConversionRate = (fraction?: number | null): string => formatPercent(fraction, FORMAT_DECIMAL_PLACE);

export const baseFields: readonly FieldsConfig[] = [
    defineField({
        key: "country",
        getAnnotation: ({ data }) =>
            data.locationStr ? { iconName: "location", content: data.locationStr } : undefined,
    }),
    defineField({ key: "headcount" }),
    defineField({ key: "revenue" }),
    defineField({ key: "businessModel" }),
];

export const baseFieldsNoFoundingNoEcommerce: readonly FieldsConfig[] = [
    ...baseFields,
    defineField({ key: "siteTypes", render: renderSiteTypes }),
    defineField({ key: "companyFoundingYear" }),
];

export const baseFieldsNoFounding: readonly FieldsConfig[] = [
    ...baseFields,
    defineField({ key: "companyFoundingYear" }),
];

export const baseFieldsNoEcommerce: readonly FieldsConfig[] = [
    ...baseFields,
    defineField({ key: "siteTypes", render: renderSiteTypes }),
];

export const foundingFields: readonly FieldsConfig[] = [
    defineField({ key: "companyFoundingYear" }),
    defineField({ key: "totalFundingAmount", render: renderFundingAmount }),
    defineField({ key: "lastFundingRoundDate", render: renderFundingDate }),
    defineField({ key: "lastFundingRoundType" }),
];

const ECOMMERCE_FIELDS_WITHOUT_CR: readonly FieldsConfig[] = [
    defineField({ key: "siteTypes", render: () => "Ecommerce" }),
    defineField({ key: "transactionsRange" }),
    defineField({ key: "onlineRevenueRange" }),
    defineField({ key: "ecommerceType", render: renderEcommerceType }),
];

export const getEcommerceFields = (includeConversionRate: boolean): readonly FieldsConfig[] =>
    includeConversionRate
        ? ECOMMERCE_FIELDS_WITHOUT_CR.toSpliced(
              2,
              0,
              defineField({
                  key: "conversionRate",
                  render: renderConversionRate,
                  getAnnotation: () => ({
                      iconName: "info",
                      content: translate("si.company-analysis.company-data.card.tooltip.conversionRate"),
                  }),
              }),
          )
        : ECOMMERCE_FIELDS_WITHOUT_CR;
